import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { EpocusProgramStatus } from "elephant-web/gql";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../_lib/epocus-palette";
import { linkToEpocusOnboarding } from "../links";
import { SettingsCard } from "../settings/_lib/settings-card";
import { useEpocusAdminProgramsQuery } from "./_lib/__generated__/epocus-admin-operations.gql";
import {
  AdminList,
  AdminListLinkRow,
  AdminListScroll,
} from "./_lib/admin-list";
import { AdminPageHeader } from "./_lib/admin-page-header";
import { AdminLoadingRow, AdminPageRoot } from "./_lib/admin-page-root";
import { CreateProgramAdminDialog } from "./_lib/create-program-admin-dialog";
import { CreateProgramDialog } from "./_lib/create-program-dialog";
import { linkToEpocusAdminProgram } from "./links";

const PAGE_SIZE = 8;

const ProgramName = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const ProgramDetail = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const AeTitle = styled("span")({
  fontFamily: "monospace",
  letterSpacing: "0.06em",
});

const HeaderRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
});

const HeaderActions = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
});

const PagerRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "8px 16px",
  borderTop: `1px solid ${EPOCUS_BORDER}`,
});

const PagerLabel = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const MutedBody = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

export const EpocusAdminProgramsPage: FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [onboardingEnabled] = useFeatureFlag(
    featureFlags.epocus.hackathonSept26,
  );

  const { data, loading, error, refetch } = useEpocusAdminProgramsQuery({
    fetchPolicy: "cache-and-network",
  });
  const programs = data?.epocusPrograms;

  const total = programs?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = useMemo(
    () =>
      (programs ?? []).slice(
        currentPage * PAGE_SIZE,
        currentPage * PAGE_SIZE + PAGE_SIZE,
      ),
    [programs, currentPage],
  );

  return (
    <AdminPageRoot>
      <HeaderRow>
        <AdminPageHeader
          title="Admin"
          subtitle="Create ePOCUS programs and manage the people in them"
        />
        <HeaderActions>
          {onboardingEnabled && (
            <Button
              component={Link}
              to={linkToEpocusOnboarding()}
              variant="outlined"
              color="primary"
            >
              Program setup assistant
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setAdminDialogOpen(true)}
          >
            Create program admin
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setDialogOpen(true)}
          >
            New program
          </Button>
        </HeaderActions>
      </HeaderRow>

      {error && data && (
        <SettingsCard>
          <MutedBody role="alert">
            Couldn't refresh programs — showing the last loaded list.
          </MutedBody>
        </SettingsCard>
      )}

      {loading && !data ? (
        <AdminLoadingRow>
          <CircularProgress aria-label="Loading programs" />
        </AdminLoadingRow>
      ) : error && !data ? (
        <SettingsCard>
          <MutedBody role="alert">Couldn't load programs.</MutedBody>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => void refetch()}
            style={{ alignSelf: "flex-start", minHeight: 44 }}
          >
            Try again
          </Button>
        </SettingsCard>
      ) : total === 0 ? (
        <SettingsCard>
          <MutedBody>No programs yet. Create the first one.</MutedBody>
        </SettingsCard>
      ) : (
        <AdminList>
          <AdminListScroll>
            {visible.map((program) => (
              <AdminListLinkRow
                key={program.id}
                to={linkToEpocusAdminProgram(program.id)}
              >
                <ProgramName>
                  {program.name}
                  {program.status !== EpocusProgramStatus.Active &&
                    " · Disabled"}
                </ProgramName>
                <ProgramDetail>
                  {program.dimseAeTitle ? (
                    <>
                      AE title <AeTitle>{program.dimseAeTitle}</AeTitle>
                    </>
                  ) : (
                    "No AE title"
                  )}
                </ProgramDetail>
              </AdminListLinkRow>
            ))}
          </AdminListScroll>
          {pageCount > 1 && (
            <PagerRow>
              <PagerLabel>
                {`${currentPage * PAGE_SIZE + 1}–${currentPage * PAGE_SIZE + visible.length} of ${total}`}
              </PagerLabel>
              <Box display="flex" gridGap={8}>
                <Button
                  size="small"
                  disabled={currentPage === 0}
                  onClick={() => setPage(currentPage - 1)}
                  style={{ textTransform: "none" }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  disabled={currentPage >= pageCount - 1}
                  onClick={() => setPage(currentPage + 1)}
                  style={{ textTransform: "none" }}
                >
                  Next
                </Button>
              </Box>
            </PagerRow>
          )}
        </AdminList>
      )}

      <CreateProgramDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />

      <CreateProgramAdminDialog
        open={adminDialogOpen}
        onClose={() => setAdminDialogOpen(false)}
      />
    </AdminPageRoot>
  );
};
